import React from 'react';
import { BookmarkIcon, CheckIcon, ShareIcon } from '@heroicons/react/outline';
import { useTranslation } from 'next-i18next';
import useWhislist from '@/hooks/useWhislist';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { withOrientationChange } from 'react-device-detect';
import { arrayJoinKeyName } from 'utils';

const VideoPlayer = dynamic(() => import('@/components/VideoPlayer/Plyr'), {
  ssr: false,
});

type Props = {
  content: any;
  onShare?: () => void;
};

const ContentDetail: React.FC<Props> = ({ content, onShare }: Props) => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const { isOnWhislist, toggleContent } = useWhislist();

  return (
    <div className="flex flex-col w-full">
      <div className="relative overflow-hidden rounded-lg transition-transform duration-300 ease-in-out mb-6 md:mb-12 md:h-auto">
        <div
          className={`w-full transition-opacity duration-1000 ease-out opacity-0 !opacity-100`}
        >
          <VideoPlayer
            poster={content.bg?.url}
            source={content.finalvideoencodes?.final_url_20}
            content={content}
          />
        </div>
      </div>
      <div className="flex-col mt-3 space-y-3 w-full lg:flex">
        <div className="flex justify-between items-center">
          <div className="space-y-1 flex-1 pr-2">
            <div className="text-xl">{content.film_title}</div>
            <div className="font-thin flex text-xs md:text-sm w-full items-center space-x-1 divide-x-2 divide-accent divide-opacity-50">
              <div className="pr-2 w-auto">
                {Array.isArray(content[`genre_${locale}`])
                  ? content[`genre_${locale}`].join('  ')
                  : content[`genre_${locale}`]}
              </div>
              <div className="px-2 w-auto">{content.production_year}</div>
              <div className="px-2 w-auto">
                {arrayJoinKeyName(content.countries, `name_${locale}`)}
              </div>
            </div>
          </div>
          <div className="flex space-x-3 self-center">
            <button
              className="transition-opacity duration-300 flex rounded-xl h-11 px-4 lg:px-5 bg-white bg-opacity-10 items-center justify-center space-x-2 font-semibold lg:text-xs opacity-90 hover:opacity-100 active:opacity-100 touch-manipulation"
              onClick={(event) => toggleContent(event, content)}
            >
              {!isOnWhislist(content) ? (
                <>
                  <BookmarkIcon className="w-6 h-6 md:w-6 md:h-6" />
                  <span className="hidden lg:block">{t('queue')}</span>
                </>
              ) : (
                <>
                  <CheckIcon className="w-6 h-6 md:w-6 md:h-6" />
                  <span className="hidden lg:block">{t('queued')}</span>
                </>
              )}
            </button>

            <button
              className="transition-opacity duration-300 flex rounded-xl h-11 px-4 lg:px-5 bg-accent items-center justify-center space-x-2 font-semibold lg:text-xs opacity-90 hover:opacity-100 active:opacity-100 touch-manipulation"
              onClick={onShare}
            >
              <ShareIcon className="w-6 h-6 md:w-6 md:h-6" />
              <span className="hidden lg:block">{t('common:share.share')}</span>
            </button>
          </div>
        </div>
        <p className="font-thin leading-5 pt-2 text-xs md:text-sm">
          {content[`description_${locale}`]}
        </p>
        <div className="leading-5 flex items-center space-x-3 text-xs md:text-sm">
          <span className="text-gray-500 font-semibold">{t('director')}</span>
          <span className="font-thin">{content.director}</span>
        </div>
        <div className="leading-5 items-center space-x-3 text-xs md:text-sm">
          <span className="text-gray-500 font-semibold">{t('starring')}</span>
          <span className="font-thin">{content.cast}</span>
        </div>
      </div>
    </div>
  );
};

export default withOrientationChange(ContentDetail);
